import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import '../styles/captcha.css';

const Captcha = ({ onValidate }) => {
  const [captchaText, setCaptchaText] = useState('');
  const [userInput, setUserInput] = useState('');
  const [isValid, setIsValid] = useState(false);

  const generateCaptcha = useCallback(() => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let result = '';
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptchaText(result);
    setUserInput('');
    setIsValid(false);
    onValidate(false);
  }, [onValidate]);

  useEffect(() => {
    generateCaptcha();
  }, [generateCaptcha]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setUserInput(value);
    const valid = value === captchaText;
    setIsValid(valid);
    onValidate(valid);
  };

  const handleRefresh = () => {
    generateCaptcha();
  };

  return (
    <div className="captcha-container">
      <label className="captcha-label">Security Verification *</label>
      <div className="captcha-wrapper">
        <div className="captcha-display">
          <canvas
            ref={(canvas) => {
              if (canvas && captchaText) {
                const ctx = canvas.getContext('2d');
                canvas.width = 180;
                canvas.height = 60;
                
                // Background with noise
                ctx.fillStyle = '#1a1a1a';
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                
                // Add noise lines
                for (let i = 0; i < 5; i++) {
                  ctx.strokeStyle = `rgba(0, 102, 255, ${Math.random() * 0.3})`;
                  ctx.lineWidth = 1;
                  ctx.beginPath();
                  ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
                  ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
                  ctx.stroke();
                }
                
                // Draw captcha text
                ctx.font = 'bold 32px Arial';
                ctx.textBaseline = 'middle';
                
                // Draw each character with random rotation and color
                for (let i = 0; i < captchaText.length; i++) {
                  const char = captchaText[i];
                  const x = 20 + i * 27;
                  const y = 30;
                  
                  ctx.save();
                  ctx.translate(x, y);
                  ctx.rotate((Math.random() - 0.5) * 0.4);
                  
                  // Random color between blue and green
                  const colors = ['#0066FF', '#00A86B', '#FFFFFF'];
                  ctx.fillStyle = colors[Math.floor(Math.random() * colors.length)];
                  
                  ctx.fillText(char, 0, 0);
                  ctx.restore();
                }
                
                // Add more noise
                for (let i = 0; i < 100; i++) {
                  ctx.fillStyle = `rgba(255, 255, 255, ${Math.random() * 0.3})`;
                  ctx.fillRect(
                    Math.random() * canvas.width,
                    Math.random() * canvas.height,
                    1,
                    1
                  );
                }
              }
            }}
          />
        </div>
        <button
          type="button"
          className="captcha-refresh"
          onClick={handleRefresh}
          aria-label="Refresh Captcha"
        >
          <RefreshCw size={20} />
        </button>
      </div>
      <input
        type="text"
        className={`captcha-input ${isValid ? 'valid' : userInput ? 'invalid' : ''}`}
        placeholder="Enter the code above"
        value={userInput}
        onChange={handleInputChange}
        maxLength={6}
        required
      />
      {userInput && (
        <p className={`captcha-feedback ${isValid ? 'valid' : 'invalid'}`}>
          {isValid ? '✓ Code verified' : '✗ Code does not match'}
        </p>
      )}
    </div>
  );
};

export default Captcha;
